import React, { useEffect, useState } from 'react';
import { Card, Space, Button, Table, Form, Input, Modal, Popconfirm, Tag, message, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, CheckCircleOutlined, BranchesOutlined } from '@ant-design/icons';
import apiService, { admin } from '../../services/api';
import ZplCanvasRenderer from '../../components/ZplCanvasRenderer';

const { TextArea } = Input;

export default function LabelTemplatesManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createVisible, setCreateVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [versionVisible, setVersionVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [versionForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const res = await admin.getLabelTemplates({});
      setItems(res.data || []);
    } catch (e) {
      message.error(e.message || 'Erro ao listar modelos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await admin.createLabelTemplate(values);
      setCreateVisible(false);
      form.resetFields();
      await load();
      message.success('Modelo criado');
    } catch (e) {
      if (e?.message) message.error(e.message);
    }
  };

  const handleEdit = async () => {
    try {
      const values = await form.validateFields();
      await admin.updateLabelTemplate(editingItem.id, { code: values.code });
      setEditVisible(false);
      setEditingItem(null);
      form.resetFields();
      await load();
      message.success('Modelo atualizado');
    } catch (e) {
      if (e?.message) message.error(e.message);
    }
  };

  const handleDelete = async (item) => {
    try {
      await admin.deleteLabelTemplate(item.id);
      await load();
      message.success('Modelo removido');
    } catch (e) {
      message.error(e.message || 'Erro ao remover');
    }
  };

  const openVersionModal = (item) => {
    setEditingItem(item);
    setVersionVisible(true);
    versionForm.setFieldsValue({ version: '', code: item.code });
  };

  const handleCreateVersion = async () => {
    try {
      const vals = await versionForm.validateFields();
      await admin.createLabelTemplateVersion(editingItem.id, vals);
      setVersionVisible(false);
      setEditingItem(null);
      versionForm.resetFields();
      await load();
      message.success('Nova versío criada e ativada');
    } catch (e) {
      message.error(e.message || 'Erro ao criar versío');
    }
  };

  const handleActivate = async (item) => {
    try {
      await admin.activateLabelTemplate(item.id);
      await load();
      message.success('Versão ativada');
    } catch (e) {
      message.error(e.message || 'Erro ao ativar');
    }
  };

  const columns = [
    { title: 'Nome', dataIndex: 'name', key: 'name' },
    { title: 'Versão', dataIndex: 'version', key: 'version' },
    { title: 'Ativo', dataIndex: 'isActive', key: 'isActive', render: (v) => (
      <Tag color={v ? 'green' : 'default'}>{v ? 'Ativo' : 'Inativo'}</Tag>
    ) },
    { title: 'Atualizado em', dataIndex: 'updatedAt', key: 'updatedAt', render: (d) => new Date(d).toLocaleString() },
    {
      title: 'Ações', key: 'actions', render: (_, item) => (
        <Space>
          <Button icon={<CheckCircleOutlined />} type={item.isActive ? 'default' : 'primary'} onClick={() => handleActivate(item)}>Ativar</Button>
          <Button icon={<BranchesOutlined />} disabled={!item.isActive} onClick={() => openVersionModal(item)}>Nova Versão</Button>
          <Button icon={<EditOutlined />} disabled={!item.isActive} onClick={() => {
            setEditingItem(item);
            setEditVisible(true);
            form.setFieldsValue({ name: item.name, version: item.version, code: item.code });
          }}>Editar</Button>
          <Popconfirm title="Remover modelo?" onConfirm={() => handleDelete(item)}>
            <Button danger icon={<DeleteOutlined />}>Remover</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setCreateVisible(true); form.resetFields(); }}>Novo Modelo</Button>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
      </Space>
      <Card>
        <Table rowKey="id" dataSource={items} columns={columns} loading={loading} pagination={{ pageSize: 10 }} />
      </Card>

      <Modal open={createVisible} title="Novo Modelo" onOk={handleCreate} onCancel={() => setCreateVisible(false)} okText="Criar" style={{top: 5}} maskClosable={false}>
            <Form form={form} layout="vertical">
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="name" label="Nome" rules={[{ required: true, message: 'Informe o nome' }]}> 
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="version" label="Versão" rules={[{ required: true, message: 'Informe a versío' }]}> 
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="code" label="Código (string)" rules={[{ required: true, message: 'Informe o código' }]}> 
                <TextArea rows={14} style={{ fontSize: 12 }} />
              </Form.Item>
            </Form>
      </Modal>

      <Modal open={editVisible} title="Editar Modelo" onOk={handleEdit} onCancel={() => { setEditVisible(false); setEditingItem(null); }} okText="Salvar" style={{top:5}} maskClosable={false} >
            <Form form={form} layout="vertical">
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="name" label="Nome">
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="version" label="Versão">
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="code" label="Código (string)" rules={[{ required: true, message: 'Informe o código' }]}> 
                <TextArea rows={16} style={{ fontSize: 12 }} />
              </Form.Item>
            </Form>
      </Modal>

      <Modal open={versionVisible} title="Nova Versão" onOk={handleCreateVersion} onCancel={() => { setVersionVisible(false); setEditingItem(null); }} okText="Criar versío" style={{top: 5}} maskClosable={false} >
            <Form form={versionForm} layout="vertical">
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Modelo">
                    <Input value={editingItem?.name || ''} disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="version" label="Versão" rules={[{ required: true, message: 'Informe a versío' }]}> 
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="code" label="Código (string)" rules={[{ required: true, message: 'Informe o código' }]}> 
                <TextArea rows={16} style={{ fontSize: 12 }} />
              </Form.Item>
            </Form>
      </Modal>
    </div>
  );
}